import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import {
    Tabs,
    Card,
    Form,
    Input,
    Button,
    Table,
    Radio,
    Row,
    Col,
    DatePicker,
} from "antd";
import {
    User,
    Activity,
    ClipboardList,
    Printer,
    Save,
    Flame,
} from "lucide-react";
import dayjs from "dayjs";
import { useState } from "react";
import Layout from '../../Layout'


const { TextArea } = Input;

export default function MikoReceptionPage() {
  const { t } = useTranslation();
    const [form] = Form.useForm();

    const healthList = [
        t('miko.health_kidney_liver'),
        t('miko.health_diabetes'),
        t('miko.health_blood_pressure'),
        t('miko.health_hepatitis'),
        t('miko.health_cardiovascular'),
        t('miko.health_epilepsy'),
        t('miko.health_other'),
    ];

    interface HealthRecord {
        key: number;
        name: string;
        yes?: string;
        no?: string;
        note?: string;
    }

    interface ColumnConfig {
        title: string;
        dataIndex?: string;
        width?: number;
        render?: (text: any, record: HealthRecord) => React.ReactNode;
    }

    const data = healthList.map((item, index) => ({
        key: index,
        name: item,
    }));

    const [healthState, setHealthState] = useState<Record<number, string>>(
        data.reduce((acc, item) => {
            acc[item.key] = "no"; // mặc định Không
            return acc;
        }, {} as Record<number, string>)
    );

    const columns: ColumnConfig[] = [
        {
            title: t('miko.col_disease'),
            dataIndex: "name",
            width: 200,
        },
        {
            title: t('miko.col_yes'),
            width: 80,
            align: "center",
            render: (_, record: HealthRecord) => (
                <Radio
                    checked={healthState[record.key] === "yes"}
                    onChange={() =>
                        setHealthState((prev) => ({
                            ...prev,
                            [record.key]: "yes",
                        }))
                    }
                />
            ),
        },
        {
            title: t('miko.col_no'),
            width: 80,
            align: "center",
            render: (_, record: HealthRecord) => (
                <Radio
                    checked={healthState[record.key] === "no"}
                    onChange={() =>
                        setHealthState((prev) => ({
                            ...prev,
                            [record.key]: "no",
                        }))
                    }
                />
            ),
        },
        {
            title: t('miko.col_explain'),
            render: (_, record: HealthRecord) => (
                <Input
                    disabled={healthState[record.key] !== "yes"}
                    className={
                        healthState[record.key] !== "yes"
                            ? "opacity-60"
                            : ""
                    }
                />
            ),
        },
    ];

    const items = [
        {
            key: "1",
            label: (
                <span className="flex items-center gap-2">
                    <User size={16} />
                    {t('miko.tab_history')}
                </span>
            ),

            children: (
                <Card className="dark:bg-[#1f1f1f] shadow-sm">
                    <Form layout="vertical">
                        <Form.Item
                            label={t('miko.full_name')}
                            name="name"
                            rules={[{ required: true, message: t('miko.full_name_required') }]}
                        >
                            <Input />
                        </Form.Item>
                    </Form>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button icon={<Printer size={16} />}>{t('miko.print')}</Button>

                        <Button type="primary" icon={<Save size={16} />}>
                            {t('miko.save')}
                        </Button>
                    </div>
                </Card>
            ),
        },

        {
            key: "2",
            label: (
                <span className="flex items-center gap-2">
                    <Activity size={16} />
                    {t('miko.tab_health')}
                </span>
            ),

            children: (
                <Card className="dark:bg-[#1f1f1f] shadow-sm">
                    <Table
                        columns={columns}
                        dataSource={data}
                        pagination={false}
                        className="custom-ant-table"
                        scroll={{ x: "max-content" }}
                    />

                    <div className="flex justify-end gap-3 mt-4">
                        <Button icon={<Printer size={16} />}>{t('miko.print')}</Button>

                        <Button type="primary" icon={<Save size={16} />}>
                            {t('miko.save')}
                        </Button>
                    </div>
                </Card>
            ),
        },

        {
            key: "3",
            label: (
                <span className="flex items-center gap-2">
                    <ClipboardList size={16} />
                    {t('miko.tab_doctor')}
                </span>
            ),

            children: (
                <Card className="dark:bg-[#1f1f1f] shadow-sm">
                    <Form layout="vertical">
                        <Form.Item label={t('miko.doctor_instruction')}>
                            <TextArea rows={4} />
                        </Form.Item>
                    </Form>

                    <div className="flex justify-end gap-3 mt-4">
                        <Button icon={<Printer size={16} />}>{t('miko.print')}</Button>

                        <Button type="primary" icon={<Save size={16} />}>
                            {t('miko.save')}
                        </Button>
                    </div>
                </Card>
            ),
        },
    ];

    return (
        <Layout>
            <Helmet>
                <title>{t('page.miko_reception')} | Khothi</title>
            </Helmet>
            {/* HEADER */}

            <div className="flex items-center justify-between mb-6 flex-wrap gap-4">
                <div className="flex items-center gap-3">
                    <div className="w-10 h-10 rounded-lg bg-[#3e91ab] flex items-center justify-center text-white">
                        <Flame size={20} />
                    </div>

                    <div>
                        <h1 className="text-lg font-semibold dark:text-gray-100 text-000000e0">
                            {t('miko.page_title')}
                        </h1>

                        <p className="text-sm text-gray-500">
                            {t('miko.page_subtitle')}
                        </p>
                    </div>
                </div>

                <DatePicker defaultValue={dayjs()} format="DD/MM/YYYY" />
            </div>

            {/* TABS */}

            <Tabs items={items} />
        </Layout>
    );
}